/**
 * ส่งออกตารางเป็น PDF และ Excel — ตัวเดียวใช้ทุกหน้าจอ
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมต้องมีตัวกลาง แทนที่จะให้แต่ละหน้าจอทำเอง
 * ────────────────────────────────────────────────────────────────
 *
 * ไม่ใช่เพื่อลดโค้ดซ้ำเป็นหลัก แต่เพื่อให้ "คำเตือน" ติดไปกับไฟล์ทุกใบโดยไม่มีทางลืม
 *
 * ไฟล์ที่ดาวน์โหลดออกไปแล้วคือไฟล์ที่หลุดจากบริบทของหน้าจอ มันจะถูกส่งทางอีเมล
 * ปรินต์แจกในห้องประชุม และแนบเรื่องเสนอผู้บริหาร โดยไม่มีแถบเตือนสีแดงบนจอ
 * ตามไปด้วย ถ้าปล่อยให้แต่ละหน้าจอเขียนตัวส่งออกของตัวเอง วันหนึ่งจะมีสักหน้า
 * ที่ลืมใส่คำเตือน แล้วตารางชื่อคนที่เป็นข้อมูลสังเคราะห์จะเดินออกไปเป็นเอกสารราชการ
 *
 * `warnings` จึงเป็นฟิลด์บังคับ ไม่ใช่ตัวเลือก — ส่งอาเรย์ว่างได้ถ้าไม่มีจริง ๆ
 * แต่ต้องเป็นการตัดสินใจที่ผู้เขียนโค้ดพิมพ์ลงไปเอง ไม่ใช่ค่าที่หลุดไปเพราะไม่ได้ใส่
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมทั้งสองรูปแบบ ไม่เลือกอย่างใดอย่างหนึ่ง
 * ────────────────────────────────────────────────────────────────
 *
 * PDF   คือ "เอกสาร" — หน้าตาคงที่ทุกเครื่อง แนบเรื่องได้ ปรินต์แล้วเหมือนกัน
 *       เหมาะกับสิ่งที่จะถูกอ่าน
 * Excel คือ "ข้อมูล" — เรียงใหม่ กรอง ทำ pivot และคำนวณต่อได้
 *       เหมาะกับสิ่งที่จะถูกเอาไปทำงานต่อ
 *
 * ตารางรายชื่อในระบบนี้ถูกใช้ทั้งสองแบบจริง ๆ จึงต้องมีทั้งคู่
 */

export interface ExportColumn {
  key: string;
  label: string;
  /** ความกว้างคอลัมน์ในไฟล์ Excel (ตัวอักษร) — PDF จัดความกว้างเองตามเนื้อหา */
  width?: number;
  /** ตัวเลข — จัดชิดขวาและให้ Excel รู้ว่าคำนวณต่อได้ */
  numeric?: boolean;
}

export type ExportCell = string | number | null;

export interface ExportTable {
  /** ใช้เป็นทั้งหัวเรื่องในเอกสารและชื่อไฟล์ */
  title: string;
  subtitle?: string;
  /**
   * คำเตือนที่ต้องพิมพ์ไว้ก่อนตารางเสมอ
   *
   * ⚠ บังคับใส่ — ดูเหตุผลในหัวไฟล์ ส่ง [] ได้ถ้าตารางนี้ไม่มีอะไรต้องเตือนจริง ๆ
   */
  warnings: string[];
  columns: ExportColumn[];
  rows: Array<Record<string, ExportCell>>;
}

/** วันเวลาที่ส่งออก — ต้องอยู่ในไฟล์ เพราะข้อมูลกำลังคนเปลี่ยนทุกวัน */
function stamp(): string {
  const d = new Date();
  const th = new Intl.DateTimeFormat('th-TH', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(d);
  return `ส่งออกเมื่อ ${th}`;
}

/**
 * ชื่อไฟล์ที่ระบบไฟล์ของทุกแพลตฟอร์มรับได้
 *
 * วินโดวส์ไม่ยอมรับ \\ / : * ? " < > | และไม่ยอมให้ลงท้ายด้วยจุดหรือช่องว่าง
 * ชื่อยาวเกินก็ตัด เพราะบางหน้าจอส่งชื่อหน่วยงานเต็ม ๆ มาเป็นหัวเรื่อง
 */
export function fileName(title: string, ext: string): string {
  const safe = title
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[. ]+$/, '')
    .slice(0, 80);
  const d = new Date();
  const ymd = `${d.getFullYear() + 543}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  return `${safe || 'ตาราง'}-${ymd}.${ext}`;
}

/**
 * สัญลักษณ์นำหน้าคำเตือน
 *
 * ⚠ ห้ามใช้ ⚠ (U+26A0) ในไฟล์ที่ออก — ฟอนต์ Sarabun ชุดที่ฝังใน PDF ถูกตัดเหลือ
 *   317 อักขระ และไม่มีตัวนี้ ผลคือได้สี่เหลี่ยมเปล่าหน้าทุกบรรทัดคำเตือน
 *   โดยที่หน้าจอไม่มีอะไรผิด (เจอจริงในไฟล์ที่ผู้ใช้ส่งกลับมา)
 *   ตัวที่ตรวจแล้วว่ามี — • (U+2022) · – — · · · เลขไทย ๐-๙
 *   ที่ตรวจแล้วว่าไม่มี — ⚠ ✓ ✕
 */
const BULLET = '\u2022';

const text = (v: ExportCell): string => (v === null || v === undefined ? '—' : String(v));

/* ================================================================
   PDF
   ================================================================ */

export async function exportPdf(t: ExportTable): Promise<void> {
  /*
   * โหลด pdfmake กับฟอนต์ตอนกดปุ่มเท่านั้น — รวมกันหนักกว่า 400 KB
   * คนส่วนใหญ่เปิดระบบมาดูผังแล้วออก ไม่เคยกดบันทึกไฟล์เลย
   * (แนวเดียวกับ profilePdf.ts ซึ่งโหลดชุดเดียวกัน จึงใช้ chunk ร่วมกันได้)
   */
  const [{ default: pdfMake }, fonts] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('./sarabun.js'),
  ]);

  pdfMake.addVirtualFileSystem({
    'Sarabun-Regular.ttf': fonts.SARABUN_REGULAR,
    'Sarabun-SemiBold.ttf': fonts.SARABUN_SEMIBOLD,
  });
  pdfMake.addFonts({
    Sarabun: {
      normal: 'Sarabun-Regular.ttf',
      bold: 'Sarabun-SemiBold.ttf',
      italics: 'Sarabun-Regular.ttf',
      bolditalics: 'Sarabun-SemiBold.ttf',
    },
  });

  /*
   * ⚠ padding ของ layout ต้องเป็นศูนย์ แล้วเว้นระยะด้วย margin ของเนื้อหาแทน
   *
   * pdfmake คำนวณความกว้างคอลัมน์ '*' จากพื้นที่ที่เหลือ โดยไม่หัก padding ของ layout ออกก่อน
   * ตารางจึงกว้างเกินขอบกระดาษเท่ากับผลรวม padding พอดี แล้วคอลัมน์ขวาสุดจะถูกตัดหาย
   * โดยที่บนหน้าจอไม่มีอะไรผิด — เคยเกิดขึ้นมาแล้วใน profilePdf.ts
   */
  const layout = {
    paddingLeft: () => 0,
    paddingRight: () => 0,
    paddingTop: () => 0,
    paddingBottom: () => 0,
    hLineWidth: (i: number) => (i === 0 || i === 1 ? 0.8 : 0.4),
    vLineWidth: () => 0,
    hLineColor: (i: number) => (i <= 1 ? '#1f3864' : '#dfe3ea'),
  };

  const head = t.columns.map((c) => ({
    text: c.label,
    style: 'th',
    alignment: c.numeric ? 'right' : 'left',
    margin: [0, 6, 10, 6] as [number, number, number, number],
  }));

  const body = t.rows.map((r) =>
    t.columns.map((c) => ({
      text: text(r[c.key] ?? null),
      alignment: c.numeric ? 'right' : 'left',
      margin: [0, 5, 10, 5] as [number, number, number, number],
    })),
  );

  pdfMake
    .createPdf({
      info: { title: t.title, creator: 'YANA' },
      // แนวนอนเสมอ ตารางรายชื่อมีคอลัมน์เยอะ แนวตั้งจะบีบจนอ่านไม่ออก
      pageOrientation: 'landscape',
      pageSize: 'A4',
      pageMargins: [32, 32, 32, 40],
      defaultStyle: { font: 'Sarabun', fontSize: 9, lineHeight: 1.25 },
      styles: {
        h1: { fontSize: 16, bold: true, margin: [0, 0, 0, 2] },
        sub: { fontSize: 9.5, color: '#5a6478', margin: [0, 0, 0, 2] },
        warn: { fontSize: 9, color: '#8a4b2a', margin: [0, 0, 0, 3] },
        th: { bold: true, fontSize: 9 },
      },
      footer: (page: number, total: number) => ({
        text: `${t.title} · หน้า ${page}/${total} · ${stamp()}`,
        alignment: 'center',
        fontSize: 8,
        color: '#8b98ad',
        margin: [0, 10, 0, 0],
      }),
      content: [
        { text: t.title, style: 'h1' },
        ...(t.subtitle ? [{ text: t.subtitle, style: 'sub' }] : []),
        { text: `${stamp()} · ${t.rows.length.toLocaleString('th-TH')} รายการ`, style: 'sub' },
        // คำเตือนอยู่เหนือตารางเสมอ ไม่ใช่ท้ายเอกสารที่คนเลื่อนผ่าน
        ...(t.warnings.length > 0
          ? [{ text: t.warnings.map((w) => `${BULLET} ${w}`).join('\n'), style: 'warn', margin: [0, 6, 0, 8] }]
          : [{ text: '', margin: [0, 0, 0, 8] }]),
        {
          table: {
            headerRows: 1,
            widths: t.columns.map(() => '*'),
            body: [head, ...body],
          },
          layout,
        },
      ],
    })
    .download(fileName(t.title, 'pdf'));
}

/* ================================================================
   Excel
   ================================================================ */

export async function exportXlsx(t: ExportTable): Promise<void> {
  const ExcelJS = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'YANA';
  wb.created = new Date();

  // ชื่อชีตของ Excel ห้ามเกิน 31 ตัว และห้ามมี : \\ / ? * [ ]
  const sheetName = t.title.replace(/[:\\/?*[\]]/g, '').slice(0, 31) || 'ข้อมูล';
  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: 'frozen', ySplit: 0 }],
  });

  /*
   * ⚠ คำเตือนต้องอยู่ในชีตเดียวกับข้อมูล ไม่ใช่แยกไปชีต "อ่านก่อน"
   *
   * คนที่เปิดไฟล์ Excel มาเพื่อ copy ตารางไปวางต่อ จะไม่กดดูชีตที่สอง
   * และตารางที่ถูก copy ออกไปแล้วจะไม่มีอะไรบอกว่าข้างในเป็นข้อมูลอะไร
   */
  const preface: string[] = [t.title];
  if (t.subtitle) preface.push(t.subtitle);
  preface.push(`${stamp()} · ${t.rows.length.toLocaleString('th-TH')} รายการ`);
  for (const w of t.warnings) preface.push(`${BULLET} ${w}`);

  preface.forEach((line, i) => {
    const row = ws.addRow([line]);
    ws.mergeCells(row.number, 1, row.number, Math.max(t.columns.length, 1));
    const cell = row.getCell(1);
    cell.alignment = { vertical: 'middle', wrapText: true };
    if (i === 0) cell.font = { bold: true, size: 14 };
    else if (line.startsWith(BULLET)) cell.font = { color: { argb: 'FF9A4B1F' }, size: 10 };
    else cell.font = { color: { argb: 'FF5A6478' }, size: 10 };
  });
  ws.addRow([]);

  const headerRow = ws.addRow(t.columns.map((c) => c.label));
  headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F3864' } };
  headerRow.alignment = { vertical: 'middle', wrapText: true };

  for (const r of t.rows) {
    const row = ws.addRow(
      t.columns.map((c) => {
        const v = r[c.key];
        // ปล่อยตัวเลขเป็นตัวเลขจริง ไม่แปลงเป็นข้อความ ไม่งั้น Excel จะ sum ไม่ได้
        // ซึ่งเป็นเหตุผลหลักที่คนขอไฟล์ Excel แทน PDF ตั้งแต่แรก
        if (typeof v === 'number') return v;
        return v === null || v === undefined ? '' : String(v);
      }),
    );
    t.columns.forEach((c, i) => {
      if (c.numeric) row.getCell(i + 1).alignment = { horizontal: 'right' };
    });
  }

  t.columns.forEach((c, i) => {
    const col = ws.getColumn(i + 1);
    col.width = c.width ?? (c.numeric ? 12 : 24);
  });

  // ตัวกรองบนหัวตาราง — คนเปิด Excel มาเพื่อกรองและเรียงเป็นอันดับแรก
  ws.autoFilter = {
    from: { row: headerRow.number, column: 1 },
    to: { row: headerRow.number + t.rows.length, column: Math.max(t.columns.length, 1) },
  };
  ws.views = [{ state: 'frozen', ySplit: headerRow.number }];

  const buf = await wb.xlsx.writeBuffer();
  saveBlob(
    new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    fileName(t.title, 'xlsx'),
  );
}

/**
 * สั่งให้เบราว์เซอร์บันทึกไฟล์
 *
 * ⚠ ต้อง revokeObjectURL หลังใช้เสร็จ ไม่งั้น blob ค้างอยู่ในหน่วยความจำจนกว่าจะปิดแท็บ
 * ผู้ใช้ที่กดส่งออกหลายรอบในหน้าเดียว (ซึ่งเป็นพฤติกรรมปกติ) จะสะสมไฟล์ไว้ทั้งหมด
 */
export function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
